import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import Loading from "@acadia-ui/loading";
import Tooltip, { TOOLTIP_POSITION } from "@acadia-ui/tooltip";
import { useCallback, useMemo, useRef, useState } from "react";
import { getPrismaFinancials } from "../../../Flux/Actions/BookFilterActions";
import { pushPlacementsToPrisma, updatePlacementPrismaSyncStatus } from "../../../Flux/Actions/PlacementActions";
import MissingFinancialsModal from "../../Modals/MissingFinancialsModal";
import ErrorMessagesModal from "../../Modals/ErrorMessagesModal";
import { showToast, hideToast, TOAST_TYPE, ToastNotification } from "@acadia-ui/toast";
import BookFiltersStore from "../../../Flux/Stores/BookFiltersStore";
import { fetchOnlyPackages } from "../../../Flux/Actions/PackagesActions";
import MasterCampaignsStore from "../../../Flux/Stores/MasterCampaignsStore";
import Modal from "@acadia-ui/modal";
import { draftHLISelected } from "../../../Helpers/ActionHelpers";

const prismaErrorModalId = "prisma-push-errors";

export default function usePrismaPush({ campaignMetaData, auditLevelData, prismaAPIEnabled, setSelectedRows, refresh, dataTableApi }) {
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const missingFinancialsModal = useRef(null);

	const sendToPrisma = useCallback(() => {
		setSelectedRows((selectedRows) => {
			const placementIDs = selectedRows.map((placement) => placement.PlacementID);
			// updatePlacementPrismaSyncStatus(placementIDs);
			hideToast("prisma-error-modal-toast");
			setLoading(true);
			pushPlacementsToPrisma(placementIDs)
				.then(() => {
					showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.SUCCESS} timed={5000} message="Placements sent to prisma." />);
				})
				.catch(async (error) => {
					const errorObject = await error.details.json();
					const errorMessages = errorObject?.Errors;
					if (errorMessages) {
						if (errorMessages.length <= 3) {
							errorMessages.forEach((errorMessage) => {
								setTimeout(() => {
									showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.ERROR} message={errorMessage} />);
								}, 0);
							});
						} else {
							setErrors(errorMessages);
							showToast(
								"workbookToasts",
								<ToastNotification
									id="prisma-error-modal-toast"
									type={TOAST_TYPE.ERROR}
									message="Errors occured while pushing to Prisma"
									actionTitle="Show details"
									action={() => Modal.show(prismaErrorModalId)}
								/>
							);
						}
					} else {
						showToast("workbookToasts", <ToastNotification type={TOAST_TYPE.ERROR} message="Placement(s) not sent, please review" />);
					}
				})
				.finally(() => {
					const packageChoices = JSON.parse(JSON.stringify(BookFiltersStore.getChoicesState()));
					const auditLevelChoice = auditLevelData.find((auditLevel) => auditLevel.AuditLevelName === "Package");
					if (auditLevelChoice !== null) {
						packageChoices.auditlevel = { value: auditLevelChoice.AuditLevelID };
						fetchOnlyPackages(packageChoices, MasterCampaignsStore.getParentCampaignUniqueID()).then(() => {
							setLoading(false);
							refresh(true);
						});
					}
				});
			return selectedRows;
		});
	}, []);

	const sendToPrismaClicked = useCallback(() => {
		setLoading(true);
		getPrismaFinancials(campaignMetaData.CampaignUniqueIDCode)
			.then((response) => {
				const parsedResponse = JSON.parse(response);
				if (parsedResponse.financials) {
					// Dont display modal, move to next step
					sendToPrisma();
				} else {
					setLoading(false);
					// missingFinancialsModal.current.triggerOpenClose();
					Modal.show("missingFinancialsModal");
				}
			})
			.catch((error) => {
				console.log("error", error);
				setLoading(false);
			});
	}, [campaignMetaData, sendToPrisma]);

	const Trigger = useCallback(() => {
		if (prismaAPIEnabled) {
			return (
				<Tooltip key="action-send-to-prisma" title="Send to Prisma" position={TOOLTIP_POSITION.BOTTOM_START}>
					<Button
						// this comment exists to prevent eslint from making this unreadable.
						display={BUTTON_DISPLAY.ICON_BORDERED}
						size={BUTTON_SIZE.SMALL}
						icon="icon-notify"
						onClick={sendToPrismaClicked}
						disabled={draftHLISelected(dataTableApi)}
						data-tracking-id-alt="send-to-prisma"
					>
						Send to Prisma
					</Button>
				</Tooltip>
			);
		}
		return <></>;
	}, [sendToPrismaClicked, prismaAPIEnabled]);

	const DisplayedComponent = useCallback(
		() => (
			<>
				<MissingFinancialsModal
					missingFinancialsModal={(ref) => {
						missingFinancialsModal.current = ref;
					}}
				/>
				<ErrorMessagesModal modalId={prismaErrorModalId} errors={errors} />
				{loading && <Loading children="Sending to Prisma..." />}
			</>
		),
		[loading]
	);

	return useMemo(() => [Trigger, DisplayedComponent], [DisplayedComponent, Trigger]);
}
